import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { DropDown } from 'app/components/Common/Animation/DropDown'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { LabelCounter } from 'app/components/Common/LabelCounter'
import { LocationArrow } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import useMediaQuery from 'app/utils/useMediaQuery'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo, useEffect, useState } from 'react'

export interface PoiProps {
  title?: string
  distance?: string
}

export interface Props {
  counter?: string
  description?: string
  label?: string
  languageCode: string
  locationPageURL?: string
  map?: ImageProps
  mapMobile?: ImageProps
  poi?: PoiProps[]
  title?: string
}

export const Location = memo(function Location({
  counter,
  description,
  label,
  languageCode,
  locationPageURL,
  map,
  mapMobile,
  poi,
  title,
}: Props) {
  const isMobile = useMediaQuery('(max-width: 1199px)')

  const [mapImage, setMapImage] = useState<any>(isMobile ? mapMobile : map)

  useEffect(() => {
    isMobile ? setMapImage(mapMobile) : setMapImage(map)
  }, [mapImage, isMobile])

  return (
    <Container>
      <Wrapper dial={2}>
        <Text>
          {label ? (
            <FadeInUp delay={0.4}>
              <LabelCounter dial={5} label={label} counter={counter} />
            </FadeInUp>
          ) : null}

          {title ? <Title tag="h3" length={40} text={title} /> : null}

          {description ? (
            <FadeInUp delay={0.6}>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            </FadeInUp>
          ) : null}
          {poi ? (
            <PoiContainer>
              {poi.map((item: any, index) => (
                <FadeInUp delay={index * 0.2} key={index}>
                  <Poi dial={4} row>
                    <IconWrapper dial={5}>
                      <LocationArrow />
                    </IconWrapper>
                    <PoiText>
                      <PoiTitle>{item.title}</PoiTitle>
                      <PoiDistance>{item.distance}</PoiDistance>
                    </PoiText>
                  </Poi>
                </FadeInUp>
              ))}
            </PoiContainer>
          ) : null}
        </Text>
        {map ? (
          <Map>
            <DropDown>
              <Image {...mapImage} />
            </DropDown>
          </Map>
        ) : null}

        {locationPageURL ? (
          <FadeInUp delay={0.2}>
            <CTA
              label={useVocabularyData('look-where-we-are', languageCode)}
              URL={locationPageURL}
            />
          </FadeInUp>
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  padding: 6rem 5vw;

  @media (max-width: 1199px) {
    padding: 1.5rem;
  }
`

const Wrapper = styled(FlexBox)`
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  padding: 6rem 5vw 4.5rem;

  @media (max-width: 1199px) {
    padding: 2.25rem 1.5rem;
  }
`

const Text = styled.div`
  max-width: 59.375rem;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 4.5rem;

  @media (max-width: 1199px) {
    max-width: none;
    margin-bottom: 2.25rem;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.xl}
`

const Description = styled.div`
  ${styles.description.dark}

  margin-top: 1.5rem;
`

const Map = styled.div`
  position: relative;
  height: 80vh;
  width: 100%;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1;
    height: auto;
  }
`

const PoiContainer = styled.div`
  margin-top: 1.5rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  > div {
    margin-top: 3rem;
    align-self: center;
    justify-self: center;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Poi = styled(FlexBox)`
  min-width: 16.25rem;
`

const IconWrapper = styled(FlexBox)`
  margin-right: 1.125rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.variants.neutralLight1};
  position: relative;

  &:before {
    content: '';
    position: absolute;
    border-radius: 50%;
    width: 2.875rem;
    height: 2.875rem;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 0.25rem solid ${({ theme }) => theme.colors.variants.neutralLight3};
  }

  svg {
    fill: ${({ theme }) => theme.colors.variants.primaryDark};
    height: 0.75rem;
  }
`

const PoiText = styled.div`
  text-align: left;
`

const PoiTitle = styled.div`
  ${styles.title.small}

  line-height: 1.5rem;

  color: ${({ theme }) => theme.colors.variants.neutralDark1};
`

const PoiDistance = styled.div`
  ${styles.description.dark}

  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  line-height: 1.125rem;
`

const CTA = styled(Button)`
  margin: 2.25rem auto 0;

  @media (max-width: 1199px) {
    margin-top: 2.25rem;
  }
`
